import groupBy from 'lodash/groupBy'

import { ControlStatus } from '@/gen/controls/v1/control_status_pb'
import { Control } from '@/gen/inventory/v1/control_service_pb'

import { scoresAsValidatedControlStatus } from '@/lib/control-status'

import type { ControlRow } from '@/pages/control/ag-grid-control/types'

export type CategoryMetrics = NonNullable<ControlRow['categoryMetrics']>

const sumControlWeight = (controls: Control[]): number =>
  controls.reduce((sum, control) => sum + (control.controlWeight || 0), 0)

/**
 * Computes category metrics (validated count, total, gaps, weights) for a group of controls.
 * Shared by scope and category grouping.
 *
 * Not-applicable controls are excluded entirely: they were deliberately taken out of scope, so
 * counting them in the denominator would leave the group ratio unable to reach 100%.
 *
 * Attested counts as validated here: adherence is a score, and an attested control is closed for
 * scoring. It still reads unvalidated on the Issues tab, which is a reviewer worklist, not a score.
 */
export function computeCategoryMetrics(controls: Control[]): CategoryMetrics {
  const scopedControls = controls.filter(({ status }) => status !== ControlStatus.NOT_APPLICABLE)
  const validatedControls = scopedControls.filter(({ status }) =>
    scoresAsValidatedControlStatus(status),
  )

  return {
    totalValidated: validatedControls.length,
    total: scopedControls.length,
    gapsCount: scopedControls.filter(({ status }) => status === ControlStatus.GAP).length,
    validatedWeight: sumControlWeight(validatedControls),
    totalWeight: sumControlWeight(scopedControls),
  }
}

/** Extra fields for the group header; may be partial (e.g. categoryFilterValues with only some keys). */
export type GroupHeaderExtra = {
  scope?: ControlRow['scope'] | { id: string; name: string }
  categoryFilterValues?: Partial<ControlRow['categoryFilterValues']>
}

export type GroupControlsByKeyOptions = {
  /** Returns the group key (e.g. scope name or category name) for each control */
  getGroupKey: (control: Control) => string
  /** Prefix for header row id, e.g. "scope-" -> "scope-X-group-header" */
  headerIdPrefix?: string
  /** Build scope/categoryFilterValues (and any other header-only fields) for the group header */
  buildHeaderExtra: (groupKey: string, groupControls: Control[]) => GroupHeaderExtra
}

/**
 * Generic grouping: sort by group key then title, group, then for each group
 * build a header (with metrics and custom extra) and optionally child rows.
 * Shared by groupControlsByScope and groupControlsByCategory.
 */
export function groupControlsByKey(
  controls: Control[],
  expandedGroups: Set<string>,
  { getGroupKey, headerIdPrefix = '', buildHeaderExtra }: GroupControlsByKeyOptions,
): ControlRow[] {
  const sortedControls = [...controls].sort((a, b) => {
    const keyCompare = getGroupKey(a).localeCompare(getGroupKey(b))
    if (keyCompare !== 0) return keyCompare
    return a.title.localeCompare(b.title)
  })

  const grouped = groupBy(sortedControls, (c) => getGroupKey(c))
  const result: ControlRow[] = []
  const sortedKeys = Object.keys(grouped).sort((a, b) => a.localeCompare(b))

  for (const groupKey of sortedKeys) {
    const groupControls = grouped[groupKey]
    if (groupControls.length === 0) continue

    const isExpanded = expandedGroups.has(groupKey)
    const categoryMetrics = computeCategoryMetrics(groupControls)
    const { totalWeight } = categoryMetrics
    const headerExtra = buildHeaderExtra(groupKey, groupControls)

    result.push({
      id: `${headerIdPrefix}${groupKey}-group-header`,
      title: groupKey,
      isGroupHeader: true,
      isExpanded,
      categoryName: groupKey,
      categoryMetrics,
      categoryChildren: groupControls,
      ...headerExtra,
    } as ControlRow)

    if (isExpanded) {
      for (const control of groupControls) {
        result.push({
          ...control,
          isGroupedControl: true,
          categoryTotalWeight: totalWeight,
        } as ControlRow)
      }
    }
  }

  return result
}
